import {
  EPSPeakCalculation,
  FPMPeakCalculation,
} from '../services/peakCalculation';
import {
  ONPREM_DISCOUNT,
  ENTERPRISE_SIEM_RU_RATIO,
  ENTERPRISE_NDR_RU_RATIO,
  USAGE_SIEM_RU_RATIO,
  USAGE_NDR_RU_RATIO,
} from '../constants/spread';
import * as PRICES from '../constants/prices';

/**
 * Calculate the estimated price (peak EPS + peak FPM)
 *
 * @param {number} employees : Number of employees/workstations
 * @param {number} servers : Number of servers
 * @param {number} spread : Spread for range estimates
 * @param {boolean} isNDR : Includes NDR
 * @param {boolean} OnPrem : Includes OnPrem
 * @param {boolean} isLower : lowest or highest price range sought
 * @returns {number}
 */
export async function estimatedPrice(
  employees,
  servers,
  spread,
  isNDR,
  OnPrem,
  isLower,
) {
  const peakEPS = await EPSPeakCalculation(employees, servers);
  const peakFPM = await FPMPeakCalculation(employees, servers);

  // on Prem
  if (OnPrem) {
    return calculatedPriceOnPrem(peakEPS, peakFPM, servers, isNDR, isLower);
  }

  // on Cloud
  if (!OnPrem) {
    if (isNDR) {
      return (
        calculatedPrice(
          Math.ceil(peakEPS * spread),
          PRICES.EPS_PRICES,
          PRICES.EPS_QTY,
        ) +
        calculatedPrice(
          Math.ceil(peakFPM * spread),
          PRICES.FPM_PRICES,
          PRICES.FPM_QTY,
        )
      );
    } else {
      return calculatedPrice(
        Math.ceil(peakEPS * spread),
        PRICES.EPS_PRICES,
        PRICES.EPS_QTY,
      );
    }
  }
}

/**
 * Calculate the final price according to quantity and price per tier
 *
 * @param {number} peak : EPS or FPM peak
 * @param {number[]} prices : Array of prices per tier
 * @param {number[]} quantities : Array of quantities per tier
 * @returns {number}
 */
const calculatedPrice = (peak, PRICES, QUANTITIES) => {
  const [PRICE1, PRICE2] = PRICES;
  const [QTY_START, QTY2] = QUANTITIES;

  let price;
  let newPeak;
  switch (true) {
    case peak > QTY2:
      newPeak = peak - QTY2;
      price = QTY2 * PRICE1 + newPeak * PRICE2;
      break;
    default:
      newPeak = peak > 0 && peak < QTY_START ? QTY_START : peak;
      price = newPeak * PRICE1;
      break;
  }

  return price;
};

/**
 * Calculate the final on Prem price
 *
 * @param {number} peakEPS : EPS peak
 * @param {number} peakFPM : FPM peak
 * @param {number} servers : servers
 * @param {boolean} isNDR : NDR selected or not
 * @param {boolean} isLower : lowest or highest price range sought
 * @returns {number}
 */
const calculatedPriceOnPrem = (peakEPS, peakFPM, servers, isNDR, isLower) => {
  const scale = isLower ? 0.8 : 1.2;

  let usageSIEM_2 = Math.ceil((peakEPS * USAGE_SIEM_RU_RATIO) / 100);
  let enterpriseSIEM_2 = Math.ceil((servers * ENTERPRISE_SIEM_RU_RATIO) / 100);

  let usageSIEM = (peakEPS * USAGE_SIEM_RU_RATIO) / 100;
  let enterpriseSIEM = (servers * ENTERPRISE_SIEM_RU_RATIO) / 100;
  let calculatedPrice = 0;

  if (isNDR) {
    const usageFull = Math.ceil(
      Math.ceil((peakFPM * USAGE_NDR_RU_RATIO) / 100 + usageSIEM) * scale,
    );
    const enterpriseFull = Math.ceil(
      Math.ceil((servers * ENTERPRISE_NDR_RU_RATIO) / 100 + enterpriseSIEM) *
        scale,
    );

    calculatedPrice = usageFull < enterpriseFull ? usageFull : enterpriseFull;
  } else {
    usageSIEM = Math.ceil(usageSIEM_2 * scale);
    enterpriseSIEM = Math.ceil(enterpriseSIEM_2 * scale);

    calculatedPrice = usageSIEM < enterpriseSIEM ? usageSIEM : enterpriseSIEM;
  }

  return (
    Math.round(
      (calculatedPrice *
        PRICES.RU_MONTHLY_SUBLIST_PRICE *
        (1 - ONPREM_DISCOUNT)) /
        10,
    ) * 10
  );
};
